import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Divider from '@material-ui/core/Divider';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import Form from 'components/form';
import RadioGroup from 'components/form/Radio';
import Input from 'components/form/Input';
import Button from 'components/common/Button';
import {
  useMutation,
  Query,
  useQuery,
  useApolloClient,
  useSubscription,
} from 'react-apollo';
import { UPDATE_STUDENT_PROFILE } from 'graphql/parent/student';
import useSnack from 'hooks/useSnack';
import FAScheduleModal from 'modules/student/fa_coach/Meeting';
import { UPDATE_MILESTONE_FAEXERCISE } from 'graphql/student/milestones';
import { GET_FA_EXERCISES } from 'graphql/fa/students';
import Spin from 'components/common/Spin';
import { toUserTimezone } from 'utils';
import moment from 'moment-timezone';
import { API } from 'aws-amplify';
import withConfirm from 'hooks/withConfirm';
import { LISTEN_STUDENT_CREDITS } from 'graphql/credits';
import {
  GET_STUDENT_MILESTONES,
  INSERT_STUDENT_MILESTONE_EXERCISE,
  GET_FA_EXERCISE_ID,
  CREATE_STUDENT_MILESTONES,
} from 'graphql/admin/students';
import { GET_STUDENT_PROFILE } from 'graphql/student/Profile';
import { Avatar, makeStyles } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import PRODUCTS from 'constant/product-types';
import { ADD_CREDIT } from 'graphql/credits';

const PRODUCTS_LOOKUP = PRODUCTS.reduce((memo, i) => {
  memo[i.value] = i.label;
  return memo;
}, {});

const { FormItem } = Form;

const RadioLabel = ({ label, tip }) => (
  <Box display="flex" alignItems="center">
    <Typography color="textSecondary" style={{ marginRight: 8 }}>
      {label}
    </Typography>
    <Tooltip title={tip}>
      <InfoIcon color="disabled" fontSize="small" />
    </Tooltip>
  </Box>
);

const CounselingOptions = [
  {
    label: (
      <RadioLabel
        label="Yes"
        tip="Your student's Head Counselor and College Coach will receive notes from the discussion to ensure that the college list aligns with your family's financial expectations. Any disclosed financial information will be kept confidential and only shared with your student's WeAdmit counselors."
      />
    ),
    value: '1',
  },
  {
    label: (
      <RadioLabel
        label="No"
        tip="If you opt not to involve the Head Counselor and College Coach, the financial aid counselor will not disclose any sensitive financial information to them, but we ask that you remain involved in conversations about finalizing the college list."
      />
    ),
    value: '0',
  },
];

const useStyles = makeStyles((theme) => ({
  profileCard: {
    height: `100%`,
  },
  card: {
    marginBottom: theme.spacing(4),
  },
  cardContent: {
    padding: theme.spacing(4),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: '50%',
    border: '2px solid',
    borderColor: theme.palette.primary.light,
    marginRight: theme.spacing(3),
  },
  label: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
}));

const FinancialAid = ({
  form,
  student,
  guardians,
  confirm,
  allowCustomSlot,
}) => {
  const [updateProfile] = useMutation(UPDATE_STUDENT_PROFILE);
  const [updateTask] = useMutation(UPDATE_MILESTONE_FAEXERCISE);
  const [updateCredit] = useMutation(ADD_CREDIT);
  const snack = useSnack();
  const client = useApolloClient();
  const user = useSelector((s) => s.user.user);
  const [financial_aid_settings, setSettings] = useState(
    student.financial_aid_settings,
  );
  const [showBookModal, setShowBookModal] = useState(false);
  const { getFieldDecorator, validateFields } = form;
  const { data } = useSubscription(LISTEN_STUDENT_CREDITS, {
    variables: { userId: student.id },
  });
  const classes = useStyles();
  const isParent = user.type === 'parent';
  const isStudent = user.type === 'student';
  const isCircleCheck =
    (isParent || isStudent) && student.product_type === 'group';

  const fa_total =
    data?.usage?.find((i) => i.credit_type === 'fa')?.available || 0;

  const fa_done = Math.abs(
    data?.usage?.find((i) => i.credit_type === 'fa')?.debit || 0,
  );
  const isCreditAvailable = fa_total > fa_done;
  const [milestones, setMilestones] = useState([]);
  const [FAexId, setFAexId] = useState();
  const [task, setTask] = useState();
  const availablePeriods = {
    start: moment
      .utc()
      .add(1, 'd')
      .format(),
    end: moment
      .utc()
      .add(30, 'd')
      .format(),
  };

  const handleSubmit = () => {
    return validateFields().then((values) => {
      return updateProfile({
        variables: {
          id: student.id,
          input: {
            financial_aid_settings: values,
          },
        },
      }).then(
        () => {
          snack.success('Your preferences saved.');
          setSettings(values);
        },
        (e) => snack.error(e.message),
      );
    });
  };

  const cancelMeeting = (task, useCredit) => {
    return API.del('api', `/calendar/events/${task.response.eventId}`).then(
      () => {
        snack.success('Meeting canceled.');
        const payload = {
          id: task.id,
        };
        if (useCredit) {
          payload.input = {
            status: 'done',
            response: {
              ...task.response,
              canceled: true,
              use_credit: true,
            },
          };
        } else {
          payload.input = {
            due_date: null,
            status: null,
            response: null,
          };
        }
        return updateTask({
          variables: payload,
        }).then(() => {
          if (useCredit) {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            updateCredit({
              variables: {
                input: {
                  credit_type: task.exercise.meeting_type,
                  user_id: task.user_id,
                  amount: -1,
                  description: 'Credit usage',
                },
              },
            });
          }
          window.analytics.track('Meeting Canceled', {
            ...task,
            product: PRODUCTS_LOOKUP[student.product_type],
          });
        });
      },
      (e) => snack.error(e.message),
    );
  };

  useEffect(() => {
    fetchStudentData();
    getFAexerciseId();
  }, []);

  const getFAexerciseId = async () => {
    const { data } = await client.query({
      query: GET_FA_EXERCISE_ID,
    });
    setFAexId(data.exercises[0].id);
  };

  const fetchStudentData = () => {
    return client
      .query({
        query: GET_STUDENT_MILESTONES,
        variables: {
          userId: student.id,
        },
        fetchPolicy: 'network-only',
      })
      .then(({ data: { student_milestones } }) => {
        setMilestones(
          student_milestones.map((m) => ({
            id: m.id,
            name: m.name,
            exercises: m.student_milestone_exercises.map((e) => ({
              id: e.id,
              module: e.exercise.module_id,
              exercise: e.exercise_id,
            })),
          })),
        );
      });
  };

  return (
    <Box>
      <Query
        query={GET_FA_EXERCISES}
        fetchPolicy={'cache-and-network'}
        variables={{ userId: student.id }}
      >
        {({ data, loading }) => {
          if (!data) {
            return <Spin spinning linear />;
          }
          const { rows } = data;
          const inProgress = rows.find((i) => i.status === 'inprogress');
          const notStarted = rows.find((i) => !i.status);
          const canChange =
            !(isParent || isStudent) ||
            (inProgress &&
              moment
                .tz(inProgress.due_date, user.timezone)
                .isAfter(new Date()));
          const canCancel = !(isParent || isStudent);

          const onBookingComplete = (event, task) => {
            return updateTask({
              variables: {
                id: task.id,
                input: {
                  due_date: event.slot.start,
                  status: 'inprogress',
                  response: {
                    sub: student.sub,
                    eventId: event.id,
                    source: 'cronify',
                  },
                },
              },
            });
          };

          const onReschedule = (task) => {
            const now = moment.utc();
            const dueInHour = moment
              .tz(task.due_date, user.timezone)
              .diff(now, 'h', true);
            if (dueInHour < 24 && !canCancel) {
              confirm(() => false, {
                title: 'Warning!',
                description:
                  'Meeting cannot be rescheduled less than 24 hours before the start time. Please contact your counseling team if you need to reschedule the meeting.',
                cancellationText: 'Close',
                alert: true,
              });
            } else {
              setShowBookModal(true);
            }
          };

          const onCancelMeeting = (task) => {
            if (task.response?.eventId) {
              const now = moment.utc();
              const dueInHour = moment
                .tz(task.due_date, user.timezone)
                .diff(now, 'h', true);
              /**
               * If canceled in less than 24 hours then the meting is deemed done.
               */
              if (dueInHour < 24 && !canCancel) {
                confirm(() => cancelMeeting(task, true), {
                  title: 'Warning!',
                  confirmationText: 'Cancel Session',
                  cancellationText: 'Go Back',
                  description:
                    'Are you sure? Canceling a session less than 24 hours before the meeting time counts as a meeting credit.',
                });
              } else {
                confirm(() => cancelMeeting(task), {
                  confirmationText: 'Cancel Session',
                  cancellationText: 'Go Back',
                  description:
                    'Canceling a session cannot be undone and may result in your reserved timeslot being unavailable in the future.',
                });
              }
            } else {
              snack.info('This meeting cannot be canceled.');
            }
          };

          return (
            <>
              <Query query={GET_STUDENT_PROFILE} variables={{ id: student.id }}>
                {({ data, loading }) => {
                  const profile = data?.student_profile[0];

                  if (!profile) return <Spin spinning />;

                  return (
                    <Grid container spacing={3}>
                      <Grid item sm={8} md={6}>
                        <Card>
                          <CardContent className={classes.cardContent}>
                            <Typography variant="h5">
                              Financial Aid Information
                            </Typography>
                            {inProgress && (
                              <Box marginBottom={4} marginTop={4}>
                                <Typography variant="subtitle2">
                                  Scheduled on &nbsp;
                                  {toUserTimezone(
                                    inProgress.due_date,
                                    user.timezone,
                                  ).format('MM-DD-YYYY hh:mm A z')}
                                </Typography>
                                <Typography
                                  color="primary"
                                  variant="button"
                                  gutterBottom
                                >
                                  <a
                                    target="_blank"
                                    href={
                                      profile.group
                                        ? `/meeting/group/${profile.group.group_id}`
                                        : `/meeting/${student.sub}`
                                    }
                                  >
                                     <Button
                                    color="primary"
                                    variant="contained"
                                    size="small"
                                    onClick={() => localStorage.setItem('meeting type', 'fc')}
                                
                                  >
                                    Click here to join the meeting
                                  </Button>
                                   
                                  </a>
                                </Typography>
                                <Box marginTop={1}>
                                  <Button
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    onClick={() => onReschedule(inProgress)}
                                    disabled={!canChange}
                                  >
                                    Reschedule Session
                                  </Button>{' '}
                                  <Button
                                    color="secondary"
                                    size="small"
                                    variant="outlined"
                                    onClick={() => onCancelMeeting(inProgress)}
                                    disabled={!canChange}
                                  >
                                    Cancel Session
                                  </Button>
                                </Box>
                                <Divider style={{ marginTop: 16 }} />
                              </Box>
                            )}
                            <Box marginTop={4}>
                              <FormItem>
                                {getFieldDecorator('involve_coaches', {
                                  initialValue:
                                    financial_aid_settings?.involve_coaches,
                                  rules: [
                                    {
                                      required: true,
                                      message: 'Please select an option ',
                                    },
                                  ],
                                })(
                                  <RadioGroup
                                    label="Would you like to involve your WeAdmit counseling team?"
                                    options={CounselingOptions}
                                  />,
                                )}
                              </FormItem>
                              <FormItem>
                                {getFieldDecorator('involve_child', {
                                  initialValue:
                                    financial_aid_settings?.involve_child,
                                  rules: [
                                    {
                                      required: true,
                                      message: 'Please select an option ',
                                    },
                                  ],
                                })(
                                  <RadioGroup
                                    label="Would you like to involve your child in financial aid discussions?"
                                    hint="Some parents want to involve their child so they
                                are aware of the situation and financial
                                realities while other parents prefer to leave
                                their child out of the discussion. This is a
                                personal family decision, and WeAdmit will
                                support whatever you decide."
                                    options={[
                                      { label: 'Yes', value: '1' },
                                      { label: 'No', value: '0' },
                                    ]}
                                  />,
                                )}
                              </FormItem>

                              <FormItem>
                                {getFieldDecorator('special_situation', {
                                  initialValue:
                                    financial_aid_settings?.special_situation,
                                })(
                                  <Input
                                    multiline
                                    label="Do you have any special circumstances?"
                                    hint={
                                      <>
                                        <Typography
                                          paragraph
                                          variant="caption"
                                          className={classes.label}
                                        >
                                          If you have any special circumstances
                                          such as the following, please let us
                                          know in advance, so your financial aid
                                          counselor can prepare accordingly.
                                        </Typography>
                                        <ul>
                                          <li>- Divorced</li>
                                          <li>
                                            - Parents and/or student do not have
                                            US Citizenship or permanent
                                            residency.
                                          </li>
                                          <li>
                                            - Complex personal assets (e.g.
                                            globally diversified financial asset
                                            portfolio, significant income from
                                            foreign entities){' '}
                                          </li>
                                          <li>- Other</li>
                                        </ul>
                                      </>
                                    }
                                    rows={4}
                                  />,
                                )}
                              </FormItem>
                              <FormItem>
                                {getFieldDecorator('other', {
                                  initialValue: financial_aid_settings?.other,
                                })(
                                  <Input
                                    multiline
                                    label="Additional comments/questions"
                                    hint="Please tell us anything else you would like us
                                to share with your financial aid counselor."
                                    rows={4}
                                  />,
                                )}
                              </FormItem>

                              <Box textAlign="center">
                                <Button
                                  size="large"
                                  variant="contained"
                                  color="primary"
                                  onClick={handleSubmit}
                                >
                                  Save
                                </Button>
                              </Box>
                            </Box>
                          </CardContent>
                          {showBookModal && (
                            <FAScheduleModal
                              hasCredit={isCreditAvailable}
                              title="Book Financial Aid Session"
                              student={student}
                              onClose={() => setShowBookModal(false)}
                              allowCustomSlot={allowCustomSlot}
                              task={inProgress || notStarted || task}
                              onBookingComplete={onBookingComplete}
                              parents={guardians.map(({ guardian }) => ({
                                email: guardian.user_email,
                                display_name: `${guardian.user_first_name} ${guardian.user_last_name}`,
                              }))}
                              includeStudent={
                                financial_aid_settings?.involve_child === '1'
                              }
                              availablePeriods={availablePeriods}
                            />
                          )}
                        </Card>
                      </Grid>
                      <Grid item sm={4} md={6}>
                        <Card>
                          <CardContent className={classes.cardContent}>
                            <div className={classes.cardHeader}>
                              <Avatar
                                className={classes.avatar}
                                src={profile.fc_user?.user_pic}
                              >
                                <PersonIcon fontSize="large" />
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1">
                                  {profile.fc_user?.user_first_name}{' '}
                                  {profile.fc_user?.user_last_name}
                                </Typography>
                                <Typography color="textSecondary">
                                  Financial Aid Counselor
                                </Typography>
                              </Box>
                            </div>
                            <Typography variant="caption">
                              {profile.fc_user?.profile?.bio}
                            </Typography>
                          </CardContent>
                          <Box
                            textAlign="center"
                            className={classes.cardContent}
                          >
                            {!inProgress &&
                              isCreditAvailable &&
                              !isCircleCheck && (
                                <Button
                                  size="large"
                                  color="primary"
                                  variant="contained"
                                  onClick={async () => {
                                    if (milestones.length > 0) {
                                      const data = {
                                        milestone_id: milestones[0].id,
                                        user_id: student.id,
                                        exercise_id: FAexId,
                                        order:
                                          milestones.exercises?.length || 0,
                                      };
                                      await client
                                        .mutate({
                                          mutation: INSERT_STUDENT_MILESTONE_EXERCISE,
                                          variables: { data },
                                        })
                                        .then(({ data: { response } }) => {
                                          setTask(response);
                                        })
                                        .then(() => setShowBookModal(true));
                                    } else {
                                      const data = [
                                        {
                                          name: 'FA Session',
                                          order: 0,
                                          user_id: student.id,
                                          student_milestone_exercises: {
                                            data: [
                                              {
                                                user_id: student.id,
                                                order: 0,
                                                exercise_id: FAexId,
                                              },
                                            ],
                                          },
                                        },
                                      ];

                                      await client
                                        .mutate({
                                          mutation: CREATE_STUDENT_MILESTONES,
                                          variables: {
                                            data,
                                          },
                                        })
                                        .then(
                                          ({
                                            data: { insert_student_milestones },
                                          }) =>
                                            setTask(
                                              insert_student_milestones
                                                .returning[0]
                                                .student_milestone_exercises[0],
                                            ),
                                        )
                                        .then(() => setShowBookModal(true));
                                    }
                                  }}
                                >
                                  Schedule a session
                                </Button>
                              )}
                          </Box>
                        </Card>
                      </Grid>
                    </Grid>
                  );
                }}
              </Query>
            </>
          );
        }}
      </Query>
    </Box>
  );
};

export default withConfirm(Form.create()(FinancialAid));
